import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageBanner from "../components/PageBanner";
import SectionIntro from "../components/SectionIntro";
import api from "../lib/api";
import SmartImage from "../components/SmartImage";
import SEO from "../lib/SEO";
import { useSettings } from "../lib/SettingsContext";

export default function Shop() {
    const { settings } = useSettings();
    const [categories, setCategories] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/shop-categories")
            .then((r) => setCategories(Array.isArray(r.data) ? r.data : []))
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, []);

    return (
        <main data-testid="shop-page">
            <SEO
                title="Shop — Cake Central"
                description="Order Cake Central treats online — bento cakes, floral bouquets, cake-in-a-jar, cupcakes and vintage cakes. Sydney delivery."
                path="/shop"
            />
            <PageBanner
                eyebrow="Order online"
                title="Shop"
                image={settings?.banner_shop || "https://images.unsplash.com/photo-1535141192574-5d4897c12636?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"}
            />

            <section className="bg-[#FFF7FB] py-10 md:py-14">
                <div className="px-4 sm:px-6 lg:px-10">
                    <SectionIntro className="mb-10">
                        Boxed treats, <em>made by hand</em> and ready to order — with sweet delivery
                        across <em>Sydney</em>.
                    </SectionIntro>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 max-w-screen-2xl mx-auto">
                        {categories.map((c, i) => (
                            <Link
                                key={c.id}
                                to={`/shop/${c.slug}`}
                                className="group block relative overflow-hidden card-lift"
                                data-testid={`shop-category-${i}`}
                            >
                                <div className="aspect-[4/5] overflow-hidden bg-blush-pale">
                                    {c.cover_image_url && (
                                        <SmartImage
                                            src={c.cover_image_url}
                                            alt={c.name}
                                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                                            loading="lazy"
                                        />
                                    )}
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                                    <p className="text-[10px] tracking-[0.4em] uppercase opacity-85 mb-2">Shop</p>
                                    <h3 className="font-display text-3xl md:text-4xl leading-tight">{c.name}</h3>
                                    {c.description && (
                                        <p className="text-sm text-white/85 mt-2 line-clamp-2 max-w-sm">{c.description}</p>
                                    )}
                                    <span className="inline-flex items-center gap-2 mt-4 text-[10px] tracking-[0.3em] uppercase text-blush-pale opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                                        View products <ArrowRight size={12} />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {loaded && categories.length === 0 && (
                        <p className="text-center text-ink-soft py-20">Our shop is being updated — please check back soon.</p>
                    )}
                </div>
            </section>
        </main>
    );
}
